import { InteractableObject, ObjectInteractionMap, InteractionTypeEnum } from '../types/portfolio_types'
import * as THREE from 'three'

// NOTE: camera_position_offset is used in some objects that besides focusing (making the camera
// look at them), also change its position to end up with a better, more dramatic camera angle.
// It is determined manually by checking the camera position without any offset in the dat.gui, then
// changing it to the desired one. The offset to reach that desired number is camera_position_offset.
// We add it in Bianca.tsx's cameraFollowMe method.

const world_object_interactions: ObjectInteractionMap = {
  trainingmode_tree: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.dialog,
      dialog: [
        {
          id: 'check_project_trainingmode',
          text: 'A sign showing a videogame arcade controller rests on this tree. Should we go down the rabbit hole?',
          small_dialog: true,
          prompt_options: [
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ],
        },
      ],
    },
  },
  whoolso_tree: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.dialog,
      dialog: [
        {
          id: 'check_project_whoolso',
          text: 'A manatee logo appears on the sign of this tree. Should we go down the rabbit hole?',
          small_dialog: true,
          prompt_options: [
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ],
        },
      ],
    },
  },
  paperchat_tree: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.dialog,
      dialog: [
        {
          id: 'check_project_paperchat',
          text: 'The sign on the tree depicts a stylus drawing something. Should we go down the rabbit hole?',
          small_dialog: true,
          prompt_options: [
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ],
        },
      ],
    },
  },
  bird_in_boots_tree: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.dialog,
      dialog: [
        {
          id: 'check_project_bird_in_boots',
          text: 'The sign on the tree shows a bird that looks like a loaf of bread. Should we go down the rabbit hole?',
          small_dialog: true,
          prompt_options: [
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ],
        },
      ],
    },
  },
  ctworks_tree: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.dialog,
      dialog: [
        {
          id: 'check_job_ctworks',
          text: `There's a train on the sign attached to the tree. Should we go down the rabbit hole?`,
          small_dialog: true,
          prompt_options: [
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ],
        },
      ],
    },
  },
  realestate_tree: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.dialog,
      dialog: [
        {
          id: 'check_job_realestate',
          text: 'The sign on this tree contains a house and a GPS pin. Should we go down the rabbit hole?',
          small_dialog: true,
          prompt_options: [
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ],
        },
      ],
    },
  },
  eci_tree: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.dialog,
      dialog: [
        {
          id: 'check_job_eci',
          text: 'A shopping cart appears on the sign belonging to this tree. Should we go down the rabbit hole?',
          small_dialog: true,
          prompt_options: [
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ],
        },
      ],
    },
  },
  jump_spring_armature: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.animation,
    },
  },
  house_door: {
    inspect: {
      id: 'inspect',
      type: InteractionTypeEnum.animation,
    },
  },
}

const interactable_world_objects: { [key: string]: InteractableObject } = {
  sensor_trainingmode_tree: {
    id: 'sensor_trainingmode_tree',
    inspect_word: 'Inspect',
    on_inspect_animation: 'bianca_inspecting_rabbit_hole',
    focus_on_contact: true,
    interactions: world_object_interactions.trainingmode_tree,
  },
  sensor_whoolso_tree: {
    id: 'sensor_whoolso_tree',
    inspect_word: 'Inspect',
    on_inspect_animation: 'bianca_inspecting_rabbit_hole',
    focus_on_contact: true,
    interactions: world_object_interactions.whoolso_tree,
  },
  sensor_paperchat_tree: {
    id: 'sensor_paperchat_tree',
    inspect_word: 'Inspect',
    on_inspect_animation: 'bianca_inspecting_rabbit_hole',
    focus_on_contact: true,
    interactions: world_object_interactions.paperchat_tree,
  },
  sensor_bird_in_boots_tree: {
    id: 'sensor_bird_in_boots_tree',
    inspect_word: 'Inspect',
    on_inspect_animation: 'bianca_inspecting_rabbit_hole',
    focus_on_contact: true,
    interactions: world_object_interactions.bird_in_boots_tree,
  },
  sensor_ctworks_tree: {
    id: 'sensor_ctworks_tree',
    inspect_word: 'Inspect',
    camera_position_offset: new THREE.Vector3(8, 13, 9),
    on_inspect_animation: 'bianca_inspecting_rabbit_hole',
    focus_on_contact: true,
    interactions: world_object_interactions.ctworks_tree,
  },
  sensor_realestate_tree: {
    id: 'sensor_realestate_tree',
    inspect_word: 'Inspect',
    camera_position_offset: new THREE.Vector3(0, -11, 9),
    on_inspect_animation: 'bianca_inspecting_rabbit_hole',
    focus_on_contact: true,
    interactions: world_object_interactions.realestate_tree,
  },
  sensor_eci_tree: {
    id: 'sensor_eci_tree',
    inspect_word: 'Inspect',
    camera_position_offset: new THREE.Vector3(-24, 4, 6),
    on_inspect_animation: 'bianca_inspecting_rabbit_hole',
    focus_on_contact: true,
    interactions: world_object_interactions.eci_tree,
  },
  sensor_jump_spring: {
    id: 'sensor_jump_spring',
    inspect_word: 'Jump',
    skip_setting_position: true,
    focus_on_contact: false,
    interactions: world_object_interactions.jump_spring_armature,
  },
  sensor_house_door: {
    id: 'sensor_house_door',
    inspect_word: 'Enter',
    camera_position_offset: new THREE.Vector3(-1.5, 9, 40),
    focus_on_contact: true,
    interactions: world_object_interactions.house_door,
  },
}

export { interactable_world_objects }
